<template>
  <div class="pageContainer">
    <!-- VPN Status Banner -->
    <BannerComponent>
      <div class="vpn-status-header">
        <div class="status-indicator" :class="{ 'connected': vpnStatus.connected, 'disconnected': !vpnStatus.connected }"></div>
        <h6 class="banner-heading">VPN Status: {{ vpnStatus.connected ? 'Connected' : 'Disconnected' }}</h6>
      </div>
    </BannerComponent>

    <!-- VPN Connection Card -->
    <div class="card cardBackground">
      <div class="vpn-connection-section">
        <div class="connection-status">
          <h5 v-if="vpnStatus.connected" class="status-text connected">
            <font-awesome-icon icon="shield-alt" class="status-icon" />
            Connected to {{ vpnStatus.serverName || 'Kovo Home Mobile' }}
          </h5>
          <h5 v-else class="status-text disconnected">
            <font-awesome-icon icon="shield-alt" class="status-icon" />
            VPN Disconnected
          </h5>
          
          <p v-if="vpnStatus.connected && vpnStatus.connectedAt" class="connection-time">
            Connected since: {{ formatConnectionTime(vpnStatus.connectedAt) }}
          </p>
        </div>

        <div class="connection-controls">
          <button 
            @click="toggleVpnConnection" 
            :disabled="isConnecting || isDisconnecting"
            :class="vpnStatus.connected ? 'dangerButton' : 'primaryButton'"
            class="vpn-toggle-btn"
          >
            <font-awesome-icon 
              v-if="isConnecting || isDisconnecting" 
              icon="spinner" 
              spin 
              class="btn-icon" 
            />
            <font-awesome-icon 
              v-else-if="vpnStatus.connected" 
              icon="stop" 
              class="btn-icon" 
            />
            <font-awesome-icon 
              v-else 
              icon="play" 
              class="btn-icon" 
            />
            {{ getConnectionButtonText() }}
          </button>
          
          <button 
            @click="connectTestVpn" 
            :disabled="isConnecting || isDisconnecting || vpnStatus.connected"
            class="vpn-toggle-btn test-btn"
          >
            <font-awesome-icon 
              v-if="isConnecting || isDisconnecting" 
              icon="spinner" 
              spin 
              class="btn-icon" 
            />
            <font-awesome-icon 
              v-else 
              icon="flask" 
              class="btn-icon" 
            />
            Test Connection
          </button>
        </div>

        <!-- Connection Stats -->
        <div v-if="vpnStatus.connected" class="connection-stats">
          <div class="stat-item">
            <span class="stat-label">Data Received:</span>
            <span class="stat-value">{{ formatBytes(vpnStatus.bytesIn || 0) }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">Data Sent:</span>
            <span class="stat-value">{{ formatBytes(vpnStatus.bytesOut || 0) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- VPN Settings Banner -->
    <BannerComponent>
      <button
        class="banner-toggle-btn"
        @click="toggleSettings"
        :aria-expanded="settingsOpen"
      >
        <font-awesome-icon
          :icon="['fas', settingsOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">VPN Settings</h6>
    </BannerComponent>

    <!-- VPN Settings Card -->
    <div class="card cardBackground" v-if="settingsOpen">
      <div class="cardLine">
        <div class="setting-item">
          <div class="setting-info">
            <p class="setting-title">Always-on VPN</p>
            <p class="setting-description">Automatically reconnect when network changes</p>
          </div>
          <CustomSwitch 
            v-model="settings.alwaysOn" 
            @input="updateVpnSettings"
          />
          <span v-if="systemVpnSettings.alwaysOnCapable" class="setting-badge app-level">App-Level</span>
        </div>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <div class="setting-item">
          <div class="setting-info">
            <p class="setting-title">Block connections without VPN</p>
            <p class="setting-description">Prevents data leaks when VPN is disconnected</p>
          </div>
          <CustomSwitch 
            v-model="settings.blockWithoutVpn" 
            @input="updateVpnSettings"
          />
          <span v-if="systemVpnSettings.alwaysOnCapable" class="setting-badge app-level">App-Level</span>
        </div>
      </div>
      <div class="lineDiv"></div>
      <div class="cardLine">
        <div class="setting-item">
          <div class="setting-info">
            <p class="setting-title">Server Information</p>
            <p class="setting-description" v-if="serverInfo.endpoint">
              Endpoint: {{ serverInfo.endpoint }}
            </p>
            <p class="setting-description" v-else>
              No server information available
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- VPN Settings Instructions -->
    <VpnSettingsInstructions 
      v-if="showInstructions && systemVpnSettings.alwaysOnCapable"
      :app-name="'Kovo Home Mobile'"
      :show-version-specific="true"
      @error="showError"
    />

    <!-- Permission Required Banner -->
    <div v-if="permissionRequired" class="permission-banner">
      <div class="permission-content">
        <font-awesome-icon icon="shield-alt" class="permission-icon" />
        <div class="permission-text">
          <h6>VPN Permission Required</h6>
          <p>This app needs permission to create VPN connections. You'll see a system dialog asking for permission.</p>
        </div>
        <button @click="requestVpnPermission" class="permission-button" :disabled="isRequestingPermission">
          <font-awesome-icon v-if="isRequestingPermission" icon="spinner" spin />
          {{ isRequestingPermission ? 'Requesting...' : 'Grant Permission' }}
        </button>
      </div>
    </div>

    <!-- Permission Denied Help -->
    <div v-if="permissionDenied" class="permission-denied-banner">
      <font-awesome-icon icon="exclamation-triangle" />
      <div class="permission-denied-content">
        <h6>VPN Permission Denied</h6>
        <p>To use VPN, please follow these steps:</p>
        <ol>
          <li>Go to your device Settings</li>
          <li>Navigate to Security & Privacy</li>
          <li>Find VPN settings</li>
          <li>Allow this app to use VPN services</li>
        </ol>
        <button @click="retryPermission" class="secondaryButton">
          Try Again
        </button>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-banner">
      <font-awesome-icon icon="exclamation-triangle" />
      {{ errorMessage }}
    </div>

    <!-- Loading overlay -->
    <LoadingPage :isVisible="isLoading" />

    <!-- Peer Name Input Dialog -->
    <BaseModal
      :visible="showPeerNameDialog"
      title="Create VPN Name"
      modal-width="25rem"
      :submit-label="isCreatingPeer ? 'Creating...' : 'Create Peer'"
      :submit-disabled="!peerName.trim() || isCreatingPeer"
      :cancel-show="false"
      :show-close-button="false"
      @submit="createPeerWithName"
    >
      <div class="peer-modal-content">
        <p>No VPN name found. Please enter a name for this device:</p>
        <input 
          v-model="peerName" 
          type="text" 
          placeholder="Enter peer name (e.g., My Phone)" 
          class="peer-name-input"
          @keyup.enter="createPeerWithName"
          :disabled="isCreatingPeer"
        />
      </div>
    </BaseModal>
  </div>
</template>

<script>
import {useLoginStore} from "@/stores/loginStore";
import BannerComponent from '@/components/BannerComponent.vue';
import CustomSwitch from '@/components/CustomSwitch.vue';
import VpnSettingsInstructions from '@/components/VpnSettingsInstructions.vue';
import LoadingPage from '@/components/LoadingPage.vue';
import BaseModal from '@/components/BaseModal.vue';
import { VPNAPIService } from '@/servicehandlers/VPNAPIService.js';
import { WireGuard } from '@/plugins/wireguard.js';
import { TEST_VPN_CONFIG } from '@/assets/test-vpn-config.js';

export default {
  name: 'VPNConnectionPage',
  components: {
    BannerComponent,
    CustomSwitch,
    VpnSettingsInstructions,
    LoadingPage,
    BaseModal
  },
  data() {
    return {
      vpnStatus: {
        connected: false,
        serverName: '',
        connectedAt: null,
        bytesIn: 0,
        bytesOut: 0
      },
      settings: {
        alwaysOn: false,
        blockWithoutVpn: false
      },
      serverInfo: {
        endpoint: '',
        publicKey: ''
      },
      settingsOpen: false,
      isLoading: false,
      isConnecting: false,
      isDisconnecting: false,
      isRequestingPermission: false,
      errorMessage: '',
      vpnConfig: null,
      statusTimer: null,
      permissionRequired: false,
      permissionDenied: false,
      hasCheckedPermissions: false,
      systemVpnSettings: {
        alwaysOnCapable: false,
        canDetectAlwaysOn: false,
        appAlwaysOn: false,
        appBlockWithoutVpn: false,
        systemSettingsAvailable: false
      },
      showInstructions: false,
      showPeerNameDialog: false,
      peerName: '',
      isCreatingPeer: false
    };
  },
  computed: {
    getSubscriptionType() {
      return useLoginStore().getSubscriptionType
    }
  },
  async mounted() {
    //console.clear()
    
    // Only 'device' users can access VPN functionality
    if (this.getSubscriptionType === 'device') {
      await this.checkVpnPermissions();
      await this.ensureValidPeer();
      await this.loadVpnStatus();
      await this.loadSettings();
      await this.loadSystemVpnSettings();
      this.startStatusPolling();
      this.setupVpnListener();
    }
    // 'free' users handled by route guards/subscription service
  },
  beforeDestroy() {
    if (this.statusTimer) {
      clearInterval(this.statusTimer);
    }
    WireGuard.removeAllListeners();
  },
  methods: {
    showUpgradeModal(featureName) {
      this.$router.push({
        name: 'upgrade',
        query: {
          feature: featureName,
          return: this.$route.path
        }
      })
    },
    async checkVpnPermissions() {
      try {
        console.log('[VPNConnectionPage] Checking VPN permissions...');
        const permissionStatus = await WireGuard.checkPermissions();
        console.log('[VPNConnectionPage] Permission status:', JSON.stringify(permissionStatus, null, 2));
        this.hasCheckedPermissions = true;
        
        if (!permissionStatus.granted) {
          console.log('[VPNConnectionPage] VPN permission not granted, requesting...');
          this.permissionRequired = true;
          this.permissionDenied = false;
        } else {
          console.log('[VPNConnectionPage] VPN permission already granted');
          this.permissionRequired = false;
          this.permissionDenied = false;
        }
      } catch (error) {
        console.error('[VPNConnectionPage] Failed to check VPN permissions:', error);
        this.permissionRequired = true;
      }
    },

    async requestVpnPermission() {
      this.isRequestingPermission = true;
      this.clearError();
      
      try {
        const result = await WireGuard.requestPermissions();
        
        if (result.granted) {
          this.permissionRequired = false;
          this.permissionDenied = false;
          this.showSuccess('VPN permission granted successfully!');
        } else {
          this.permissionRequired = false;
          this.permissionDenied = true;
          if (result.userDenied) {
            this.showError('VPN permission was denied. Please check the instructions below.');
          }
        }
      } catch (error) {
        this.showError('Failed to request VPN permission: ' + error.message);
      } finally {
        this.isRequestingPermission = false;
      }
    },

    async retryPermission() {
      this.permissionDenied = false;
      await this.checkVpnPermissions();
      
      if (this.permissionRequired) {
        await this.requestVpnPermission();
      }
    },

    async loadVpnStatus() {
      try {
        //console.log('[VPNConnectionPage] Loading VPN status...');
        const status = await WireGuard.getStatus();
        console.log('[VPNConnectionPage] Raw status from plugin:', JSON.stringify(status, null, 2));
        this.vpnStatus = { ...this.vpnStatus, ...status };
        console.log('[VPNConnectionPage] Updated vpnStatus:', JSON.stringify(this.vpnStatus, null, 2));
      } catch (error) {
        console.error('[VPNConnectionPage] Failed to load VPN status:', error);
      }
    },

    async loadSettings() {
      // Load settings from local storage or API
      const savedSettings = localStorage.getItem('vpnSettings');
      if (savedSettings) {
        this.settings = JSON.parse(savedSettings);
      }
    },

    async downloadVpnConfig() {
      this.isLoading = true;
      
      try {
        const peerId = useLoginStore().getPeerId;
        if (!peerId) {
          throw new Error('No peer ID available for VPN config download');
        }
        
        console.log('[VPNConnectionPage] Downloading VPN config for peer ID:', peerId);
        const vpnService = new VPNAPIService();
        const configResult = await vpnService.downloadVPNConfig(peerId, this.$router, useLoginStore());
        
        console.log('[VPNConnectionPage] VPN Config Result:', configResult);
        
        if (configResult && configResult.success !== false) {
          this.vpnConfig = configResult;
          console.log('[VPNConnectionPage] VPN configuration downloaded successfully');
          return true;
        } else {
          throw new Error(configResult?.error || 'Failed to download VPN configuration');
        }
      } catch (error) {
        console.error('[VPNConnectionPage] Error downloading VPN config:', error);
        this.showError('Failed to download VPN configuration: ' + error.message);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async toggleVpnConnection() {
      if (this.vpnStatus.connected) {
        await this.disconnectVpn();
      } else {
        await this.connectVpn();
      }
    },

    async connectVpn() {
      console.log('[VPNConnectionPage] Starting VPN connection...');
      this.isConnecting = true;
      this.clearError();

      try {
        // Check permissions first
        console.log('[VPNConnectionPage] Checking permissions before connection...');
        await this.checkVpnPermissions();
        
        if (this.permissionRequired) {
          console.log('[VPNConnectionPage] Permission required, aborting connection');
          this.isConnecting = false;
          this.showError('VPN permission required. Please grant permission first.');
          return;
        }

        // Ensure we have a valid peer before proceeding
        console.log('[VPNConnectionPage] Ensuring valid peer exists...');
        const hasPeer = await this.ensureValidPeer();
        if (!hasPeer) {
          console.log('[VPNConnectionPage] No peer available, aborting connection');
          this.isConnecting = false;
          return;
        }

        // Download VPN config if not already available
        if (!this.vpnConfig) {
          console.log('[VPNConnectionPage] No VPN config available, downloading...');
          const configDownloaded = await this.downloadVpnConfig();
          if (!configDownloaded) {
            console.log('[VPNConnectionPage] Failed to download VPN config');
            return;
          }
          console.log('[VPNConnectionPage] VPN config downloaded successfully');
        } else {
          console.log('[VPNConnectionPage] Using cached VPN config');
        }

        // Connect to VPN
        console.log('[VPNConnectionPage] Initiating VPN connection...');
        const result = await WireGuard.connect({ config: this.vpnConfig });
        console.log('[VPNConnectionPage] Connection result:', JSON.stringify(result, null, 2));
        
        if (result.success) {
          console.log('[VPNConnectionPage] VPN connection initiated successfully');
          // Wait a moment for service to update status, then check
          setTimeout(async () => {
            console.log('[VPNConnectionPage] Checking VPN status after connection...');
            await this.loadVpnStatus();
          }, 2000);
          this.showSuccess('Connected to VPN successfully!');
        } else {
          console.log('[VPNConnectionPage] VPN connection failed:', result.message);
          if (result.permissionRequired) {
            this.permissionRequired = true;
            this.showError('VPN permission required. Please grant permission and try again.');
          } else {
            throw new Error(result.message || 'Failed to connect to VPN');
          }
        }
      } catch (error) {
        console.error('[VPNConnectionPage] VPN connection error:', error);
        this.showError('Connection failed: ' + error.message);
      } finally {
        this.isConnecting = false;
        this.isLoading = false;
      }
    },

    async connectTestVpn() {
      console.log('[VPNConnectionPage] Starting test VPN connection...');
      console.log('[VPNConnectionPage] Test VPN config:', TEST_VPN_CONFIG);
      this.isConnecting = true;
      this.clearError();

      try {
        // Check permissions first
        console.log('[VPNConnectionPage] Checking permissions before test connection...');
        await this.checkVpnPermissions();
        
        if (this.permissionRequired) {
          console.log('[VPNConnectionPage] Permission required, aborting test connection');
          this.isConnecting = false;
          this.showError('VPN permission required. Please grant permission first.');
          return;
        }

        // Connect to VPN using test config
        console.log('[VPNConnectionPage] Initiating test VPN connection...');
        const result = await WireGuard.connect({ config: TEST_VPN_CONFIG });
        console.log('[VPNConnectionPage] Test connection result:', JSON.stringify(result, null, 2));
        
        if (result.success) {
          console.log('[VPNConnectionPage] Test VPN connection initiated successfully');
          // Wait a moment for service to update status, then check
          setTimeout(async () => {
            console.log('[VPNConnectionPage] Checking VPN status after test connection...');
            await this.loadVpnStatus();
          }, 2000);
          this.showSuccess('Connected to test VPN successfully!');
        } else {
          console.log('[VPNConnectionPage] Test VPN connection failed:', result.message);
          if (result.permissionRequired) {
            this.permissionRequired = true;
            this.showError('VPN permission required. Please grant permission and try again.');
          } else {
            throw new Error(result.message || 'Failed to connect to test VPN');
          }
        }
      } catch (error) {
        console.error('[VPNConnectionPage] Test VPN connection error:', error);
        this.showError('Test connection failed: ' + error.message);
      } finally {
        this.isConnecting = false;
        this.isLoading = false;
      }
    },

    async disconnectVpn() {
      console.log('[VPNConnectionPage] Starting VPN disconnection...');
      this.isDisconnecting = true;
      this.clearError();

      try {
        console.log('[VPNConnectionPage] Calling WireGuard.disconnect()...');
        const result = await WireGuard.disconnect();
        console.log('[VPNConnectionPage] Disconnect result:', JSON.stringify(result, null, 2));
        
        if (result.success) {
          console.log('[VPNConnectionPage] VPN disconnection successful');
          setTimeout(async () => {
            console.log('[VPNConnectionPage] Checking VPN status after disconnection...');
            await this.loadVpnStatus();
          }, 1000);
        } else {
          console.log('[VPNConnectionPage] VPN disconnection failed:', result.message);
          throw new Error(result.message || 'Failed to disconnect from VPN');
        }
      } catch (error) {
        console.error('[VPNConnectionPage] VPN disconnection error:', error);
        this.showError('Disconnection failed: ' + error.message);
      } finally {
        this.isDisconnecting = false;
      }
    },

    async updateVpnSettings() {
      try {
        const result = await WireGuard.configureAlwaysOn(this.settings);
        localStorage.setItem('vpnSettings', JSON.stringify(this.settings));
        
        if (result.appLevel && !result.systemLevel) {
          this.showInstructions = this.settings.alwaysOn || this.settings.blockWithoutVpn;
        }
        
        this.showSuccess('VPN settings updated successfully!');
      } catch (error) {
        this.showError('Failed to update settings: ' + error.message);
      }
    },
    
    async loadSystemVpnSettings() {
      try {
        const settings = await WireGuard.getSystemVpnSettings();
        this.systemVpnSettings = settings;
        
        // Sync app settings with what's stored
        this.settings.alwaysOn = settings.appAlwaysOn;
        this.settings.blockWithoutVpn = settings.appBlockWithoutVpn;
        
        // Show instructions if any always-on features are enabled
        this.showInstructions = this.settings.alwaysOn || this.settings.blockWithoutVpn;
      } catch (error) {
        console.error('Failed to load system VPN settings:', error);
      }
    },
    
    async refreshNetworkStatus() {
      try {
        await WireGuard.triggerNetworkCheck();
      } catch (error) {
        console.error('Failed to trigger network check:', error);
      }
    },

    setupVpnListener() {
      WireGuard.addListener('vpnStateChanged', (state) => {
        this.vpnStatus.connected = state.connected;
        if (state.error) {
          this.showError('VPN Error: ' + state.error);
        }
      });
    },

    startStatusPolling() {
      this.statusTimer = setInterval(async () => {
        // Always check status, not just when connected
        await this.loadVpnStatus();
      }, 3000); // Update every 3 seconds
    },

    toggleSettings() {
      this.settingsOpen = !this.settingsOpen;
    },

    getConnectionButtonText() {
      if (this.isConnecting) return 'Connecting...';
      if (this.isDisconnecting) return 'Disconnecting...';
      return this.vpnStatus.connected ? 'Disconnect' : 'Connect';
    },

    formatConnectionTime(timestamp) {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleString();
    },

    formatBytes(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.clearError();
      }, 5000);
    },

    showSuccess(message) {
      // You can implement a success message display here
      // For now, we'll just clear any existing errors
      this.clearError();
      console.log('Success:', message);
    },

    clearError() {
      this.errorMessage = '';
    },

    async findPeerByName() {
      try {
        const peerName = useLoginStore().getPeerName;
        if (!peerName) {
          console.log('[VPNConnectionPage] No peer name in store');
          return false;
        }
        
        this.isLoading = true;
        console.log('[VPNConnectionPage] Searching for peer with name:', peerName);
        const vpnService = new VPNAPIService();
        const peersResult = await vpnService.getPeers(this.$router, useLoginStore());
        console.log('[VPNConnectionPage] All peers result:', peersResult);
        
        if (peersResult && Array.isArray(peersResult.peers)) {
          const foundPeer = peersResult.peers.find(peer => peer.name === peerName);
          
          if (foundPeer && foundPeer.id) {
            console.log('[VPNConnectionPage] Found peer by name:', foundPeer);
            useLoginStore().setPeerId(foundPeer.id);
            console.log('[VPNConnectionPage] Saved peer ID to store:', foundPeer.id);
            return true;
          } else {
            console.log('[VPNConnectionPage] Peer name not found in peers list');
            return false;
          }
        } else {
          console.log('[VPNConnectionPage] Invalid peers result or no peers array');
          return false;
        }
      } catch (error) {
        console.error('[VPNConnectionPage] Error finding peer by name:', error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    async ensureValidPeer() {
      try {
        console.log('[VPNConnectionPage] Checking for valid peer ID...');
        const existingPeerId = useLoginStore().getPeerId;
        
        if (existingPeerId) {
          console.log('[VPNConnectionPage] Found existing peer ID:', existingPeerId);
          return true;
        }
        
        console.log('[VPNConnectionPage] No peer ID found, checking peer name...');
        const foundByName = await this.findPeerByName();
        
        if (foundByName) {
          console.log('[VPNConnectionPage] Found peer by name, peer ID now set');
          return true;
        }
        
        console.log('[VPNConnectionPage] No valid peer ID or name found, showing peer name dialog');
        this.showPeerNameDialog = true;
        return false;
      } catch (error) {
        console.error('[VPNConnectionPage] Error checking peer status:', error);
        this.showError('Failed to check peer status: ' + error.message);
        return false;
      }
    },

    async createPeerWithName() {
      if (!this.peerName.trim()) {
        this.showError('Please enter a peer name');
        return;
      }

      this.isCreatingPeer = true;
      this.clearError();

      try {
        console.log('[VPNConnectionPage] Creating peer with name:', this.peerName);
        const vpnService = new VPNAPIService();
        const peerData = { name: this.peerName.trim() };
        
        const result = await vpnService.addPeer(peerData, this.$router, useLoginStore());
        console.log('[VPNConnectionPage] Create peer result:', result);
        
        if (result && result.peer.id) {
          console.log('[VPNConnectionPage] Peer created successfully with ID:', result.peer.id);
          useLoginStore().setPeerId(result.peer.id);
          useLoginStore().setPeerName(this.peerName.trim());
          console.log('[VPNConnectionPage] Saved peer ID and name to store:', result.peer.id, this.peerName.trim());
          this.showPeerNameDialog = false;
          this.peerName = '';
          this.showSuccess('Peer created successfully!');
          
          // Now download the VPN config
          await this.downloadVpnConfig();
        } else {
          throw new Error(result?.message || 'Failed to create peer - no ID returned');
        }
      } catch (error) {
        console.error('[VPNConnectionPage] Error creating peer:', error);
        this.showError('Failed to create peer: ' + error.message);
      } finally {
        this.isCreatingPeer = false;
      }
    }
  }
};
</script>

<style scoped>
.vpn-status-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #dc3545;
}

.status-indicator.connected {
  background-color: #28a745;
}

.vpn-connection-section {
  text-align: center;
  padding: 20px;
}

.connection-status {
  margin-bottom: 20px;
}

.status-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
}

.status-text.connected {
  color: #28a745;
}

.status-text.disconnected {
  color: #dc3545;
}

.status-icon {
  font-size: 1.2em;
}

.connection-time {
  color: #6c757d;
  font-size: 0.9em;
}

.connection-controls {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.vpn-toggle-btn {
  padding: 12px 30px;
  font-size: 1.1em;
  font-weight: 600;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
}

.test-btn {
  background-color: #ffc107;
  color: #212529;
  border: 1px solid #ffc107;
  font-size: 0.95em;
  padding: 10px 25px;
}

.test-btn:hover:not(:disabled) {
  background-color: #e0a800;
  border-color: #d39e00;
}

.test-btn:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  border-color: #dee2e6;
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-icon {
  font-size: 1em;
}

.connection-stats {
  margin-top: 20px;
  display: flex;
  justify-content: space-around;
  padding: 15px;
  background-color: var(--color-card);
  border-radius: 8px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 0.9em;
  color: #6c757d;
  margin-bottom: 5px;
}

.stat-value {
  display: block;
  font-weight: 600;
  color: #28a745;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.setting-info {
  flex: 1;
}

.setting-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.setting-description {
  font-size: 0.9em;
  color: #6c757d;
  margin: 0;
}

.permission-banner {
  background-color: #d1ecf1;
  border: 1px solid #bee5eb;
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
}

.permission-content {
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
}

.permission-icon {
  font-size: 1.5em;
  color: #0c5460;
}

.permission-text {
  flex: 1;
}

.permission-text h6 {
  margin: 0 0 5px 0;
  color: #0c5460;
  font-weight: 600;
}

.permission-text p {
  margin: 0;
  color: #0c5460;
  font-size: 0.9em;
}

.permission-button {
  background-color: #17a2b8;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  justify-content: center;
}

.permission-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.permission-denied-banner {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  margin-bottom: 15px;
  padding: 15px;
  display: flex;
  gap: 15px;
  color: #721c24;
}

.permission-denied-content {
  flex: 1;
}

.permission-denied-content h6 {
  margin: 0 0 10px 0;
  color: #721c24;
  font-weight: 600;
}

.permission-denied-content p {
  margin: 0 0 10px 0;
}

.permission-denied-content ol {
  margin: 0 0 15px 20px;
  padding: 0;
}

.permission-denied-content li {
  margin-bottom: 5px;
  font-size: 0.9em;
}

.error-banner {
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 8px;
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
}


.setting-badge {
  font-size: 0.7em;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  margin-left: 8px;
}

.setting-badge.app-level {
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.setting-badge.system-level {
  background-color: #e8f5e8;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
}

.peer-modal-content p {
  margin: 0 0 15px 0;
  color: var(--text-primary);
}

.peer-name-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 1em;
  background-color: var(--color-background);
  color: var(--text-primary);
}

.peer-name-input:focus {
  outline: none;
  border-color: var(--color-card-border);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.peer-name-input:disabled {
  background-color: #f8f9fa;
  opacity: 0.6;
  cursor: not-allowed;
}
</style>